<template>
  <div class="field">
    <span class="checkbox-container">
      <BaseCheckbox
        v-bind="$attrs"
        :id="id"
        :name="name"
        :disabled="disabled"
        :value="value"
        :checked="checked"
        :aria-describedby="`${id}-error`"
        :class="{ 'checkbox--invalid': errorMessage }"
        @input="handleChange"
      />
      <slot v-if="$slots.label" name="label" />
      <label v-else :class="['label', { 'sr-only': hideLabel }]" :for="id">
        {{ label }}
      </label>
    </span>
    <small v-if="errorMessage" class="checkbox-message" :id="`${id}-error`">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script setup lang="ts">
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import { useField } from "vee-validate";

defineOptions({
  inheritAttrs: false
});

interface Props {
  id: string;
  label: string;
  name: string;
  disabled?: boolean;
  hideLabel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  hideLabel: false
});

const { value, checked, errorMessage, handleChange } = useField<boolean>(
  () => props.name,
  undefined,
  {
    type: "checkbox",
    checkedValue: true,
    uncheckedValue: false
  }
);
</script>

<style scoped lang="scss">
.field {
  display: flex;
  flex-direction: column;
}

.label {
  @include body-semi-bold;
  color: $color-text-default;
  margin-left: 0.5rem;
}

.checkbox-container {
  align-items: center;
  display: flex;
}

.checkbox--invalid {
  @include border-danger;
}

.checkbox-message {
  @include error-message;
}

.sr-only {
  @include sr-only;
}
</style>
